import React, { PropsWithChildren, useMemo, isValidElement } from 'react';
import {
  Box,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  TooltipProps,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const makeStyles = (theme: Theme) => ({
  tooltipIconBtn: {
    color: theme.palette.questionsForm.formBlue,
    padding: 0,
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
    },
    '&:hover': {
      color: theme.palette.questionsForm.formHoverState,
    },
  },
});

interface AppTooltipProps {
  title: string | React.ReactNode;
  placement?: TooltipProps['placement'];
  isInfoTooltip?: boolean;
  sx?: SxProps;
  tooltipContentSx?: SxProps;
}

const AppTooltip: React.FC<Readonly<PropsWithChildren<AppTooltipProps>>> = ({
  title,
  placement = 'bottom',
  children,
  isInfoTooltip = true,
  tooltipContentSx,
  sx,
}) => {
  const theme = useTheme();
  const styles = makeStyles(theme);

  const tooltipContent = useMemo(() => {
    if (isValidElement(title)) {
      return title;
    }

    if (typeof title === 'string') {
      return title.split('\n').map((line, idx, arr) => (
        <React.Fragment key={idx}>
          {line}
          {idx < arr.length - 1 && <br />}
        </React.Fragment>
      ));
    }

    const titleString = String(title);
    return titleString.split('\n').map((line, idx, arr) => (
      <React.Fragment key={idx}>
        {line}
        {idx < arr.length - 1 && <br />}
      </React.Fragment>
    ));
  }, [title]);

  return (
    <Tooltip title={tooltipContent} placement={placement} sx={sx} arrow>
      {isInfoTooltip ? (
        <IconButton
          aria-label="info"
          sx={{ ...styles.tooltipIconBtn, ...tooltipContentSx }}
          disableRipple
        >
          <InfoOutlinedIcon />
        </IconButton>
      ) : (
        <Box sx={{ ...tooltipContentSx }}>{children}</Box>
      )}
    </Tooltip>
  );
};

export default AppTooltip;
